import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { User } from '../../types';
import '../../styles/Layout.css';

interface LayoutProps {
  children: React.ReactNode;
  user: User | null;
  onLogout: (localOnly?: boolean) => void;
}

const Layout: React.FC<LayoutProps> = ({ children, user, onLogout }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const location = useLocation();
  
  // Check window width on mount and resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 1024;
      setIsMobile(mobile);
      if (mobile) {
        setIsSidebarOpen(false);
      }
    };
    
    // Initial check
    handleResize();
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  const handleMenuItemClick = () => {
    // Only close sidebar on mobile devices
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  const toggleSubmenu = (category: string) => {
    setExpandedMenus(prev => 
      prev.includes(category) 
        ? prev.filter(item => item !== category)
        : [...prev, category]
    );
  };
  
  // Check if a route is active (exact match or child route)
  const isRouteActive = (basePath: string) => {
    return location.pathname === basePath || location.pathname.startsWith(`${basePath}/`);
  };

  return (
    <div className="app-layout">
      {/* Mobile Header */}
      <header className="mobile-header">
        <button 
          className="sidebar-toggle"
          onClick={toggleSidebar}
          aria-label="Toggle sidebar"
        >
          <i className="fas fa-bars"></i>
        </button>
        <div className="mobile-logo">
          <h1>Primas Accounting</h1>
        </div>
        <div className="mobile-user">
          <span>{user?.name}</span>
        </div>
      </header>

      {/* Sidebar */}
      <aside className={`sidebar ${isSidebarOpen ? 'open' : 'collapsed'} ${isMobile ? 'mobile' : ''}`}>
        <div className="sidebar-header">
          <div className="logo">
            <h1>Primas Accounting</h1>
          </div>
          <button 
            className="sidebar-close"
            onClick={toggleSidebar}
            aria-label={isSidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            <i className={`fas ${isSidebarOpen ? 'fa-angle-left' : 'fa-angle-right'}`}></i>
          </button>
        </div>
        
        <nav className="sidebar-nav">
          <ul className="nav-list">
            {/* Main Navigation */}
            <li className="nav-item">
              <NavLink to="/dashboard" className={({isActive}) => isActive ? 'active' : ''} onClick={handleMenuItemClick}>
                <i className="fas fa-chart-line"></i>
                <span>Dashboard</span>
              </NavLink>
            </li>
            
            <li className="nav-item">
              <NavLink 
                to="/" 
                className={({isActive}) => (isActive && location.pathname === '/') ? 'active' : ''} 
                onClick={handleMenuItemClick}
              >
                <i className="fas fa-list"></i>
                <span>Records</span>
              </NavLink>
            </li>
            
            <li className="nav-item">
              <NavLink to="/monthly-summary" className={({isActive}) => isActive ? 'active' : ''} onClick={handleMenuItemClick}>
                <i className="fas fa-calendar-alt"></i>
                <span>Monthly Summary</span>
              </NavLink>
            </li>
            
            {/* Payroll Category */}
            {(user?.role === 'admin' || user?.role === 'hr' || user?.role === 'accountant') && (
              <li className="nav-category">
                <div 
                  className={`nav-category-header ${isRouteActive('/payroll') || isRouteActive('/monthly-salary') ? 'active' : ''}`}
                  onClick={() => toggleSubmenu('payroll')}
                >
                  <div className="category-label">
                    <i className="fas fa-money-check-alt"></i>
                    <span>Payroll</span>
                  </div>
                  <i className={`fas ${expandedMenus.includes('payroll') ? 'fa-angle-down' : 'fa-angle-right'}`}></i>
                </div>
                {expandedMenus.includes('payroll') && (
                  <ul className="nav-submenu">
                    {user?.role === 'admin' && (
                      <li className="nav-item">
                        <NavLink to="/monthly-salary" className={({isActive}) => isActive ? 'active' : ''} onClick={handleMenuItemClick}>
                          <i className="fas fa-money-bill-wave"></i>
                          <span>Monthly Salary</span>
                        </NavLink>
                      </li>
                    )}
                    <li className="nav-item">
                      <NavLink to="/payroll/employees" className={({isActive}) => isActive ? 'active' : ''} onClick={handleMenuItemClick}>
                        <i className="fas fa-users"></i>
                        <span>Employees</span>
                      </NavLink>
                    </li>
                    <li className="nav-item">
                      <NavLink to="/payroll/payroll-months" className={({isActive}) => isActive ? 'active' : ''} onClick={handleMenuItemClick}>
                        <i className="fas fa-calendar-alt"></i>
                        <span>Monthly Payroll</span>
                      </NavLink>
                    </li>
                    <li className="nav-item">
                      <NavLink to="/payroll/salary-profiles" className={({isActive}) => isActive ? 'active' : ''} onClick={handleMenuItemClick}>
                        <i className="fas fa-file-invoice-dollar"></i>
                        <span>Salary Profiles</span>
                      </NavLink>
                    </li>
                    <li className="nav-item">
                      <NavLink to="/payroll/tax-brackets" className={({isActive}) => isActive ? 'active' : ''} onClick={handleMenuItemClick}>
                        <i className="fas fa-percent"></i>
                        <span>Tax Brackets</span>
                      </NavLink>
                    </li>
                  </ul>
                )}
              </li>
            )}
            
            {/* System Category - Admin Only */}
            {user?.role === 'admin' && (
              <li className="nav-category">
                <div 
                  className={`nav-category-header ${isRouteActive('/system') ? 'active' : ''}`}
                  onClick={() => toggleSubmenu('system')}
                >
                  <div className="category-label">
                    <i className="fas fa-cogs"></i>
                    <span>System</span>
                  </div>
                  <i className={`fas ${expandedMenus.includes('system') ? 'fa-angle-down' : 'fa-angle-right'}`}></i>
                </div>
                {expandedMenus.includes('system') && (
                  <ul className="nav-submenu">
                    <li className="nav-item">
                      <NavLink to="/categories" className={({isActive}) => isActive ? 'active' : ''} onClick={handleMenuItemClick}>
                        <i className="fas fa-tags"></i>
                        <span>Categories</span>
                      </NavLink>
                    </li>
                    <li className="nav-item">
                      <NavLink to="/exchange-rates" className={({isActive}) => isActive ? 'active' : ''} onClick={handleMenuItemClick}>
                        <i className="fas fa-dollar-sign"></i>
                        <span>Exchange Rates</span>
                      </NavLink>
                    </li>
                    <li className="nav-item">
                      <NavLink to="/activity-log" className={({isActive}) => isActive ? 'active' : ''} onClick={handleMenuItemClick}>
                        <i className="fas fa-history"></i>
                        <span>Activity Log</span>
                      </NavLink>
                    </li>
                    <li className="nav-item">
                      <NavLink to="/database-backups" className={({isActive}) => isActive ? 'active' : ''} onClick={handleMenuItemClick}>
                        <i className="fas fa-database"></i>
                        <span>DB Backup</span>
                      </NavLink>
                    </li>
                  </ul>
                )}
              </li>
            )}
          </ul>
        </nav>
        
        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              <i className="fas fa-user"></i>
            </div>
            <div className="user-details">
              <span className="user-name">{user?.name}</span>
              <span className="user-role">{user?.role}</span>
            </div>
          </div>
          <div className="logout-options">
            <button onClick={() => onLogout(true)} className="logout-button local-logout">
              <i className="fas fa-user-slash"></i>
              <span>Sign out app only</span>
            </button>
            <button onClick={() => onLogout(false)} className="logout-button full-logout">
              <i className="fas fa-sign-out-alt"></i>
              <span>Sign out completely</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Sidebar toggle button is now inside the sidebar header */}

      {/* Overlay for mobile */}
      {isSidebarOpen && <div className="sidebar-overlay" onClick={closeSidebar}></div>}

      {/* Main Content */}
      <main className="main-content">
        <div className="content-wrapper">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
